import React, { useState, useEffect } from 'react';
import { Avatar, SxProps, Theme } from '@mui/material';

interface CompanyAvatarProps {
  companyName?: string;
  domain?: string;
  sx?: SxProps<Theme>;
  size?: number;
}

const CompanyAvatar: React.FC<CompanyAvatarProps> = ({ companyName, domain, sx, size = 64 }) => {
  const [faviconUrl, setFaviconUrl] = useState<string | null>(null);
  const [faviconError, setFaviconError] = useState(false);

  // Extract domain from URL if needed
  const getDomainFromUrl = (url: string): string => {
    try {
      // Add protocol if missing
      const urlWithProtocol = url.startsWith('http') ? url : `https://${url}`;
      const urlObj = new URL(urlWithProtocol);
      return urlObj.hostname.replace(/^www\./, '');
    } catch {
      // If URL parsing fails, return the original string cleaned up
      return url.replace(/^(https?:\/\/)?(www\.)?/, '').split('/')[0];
    }
  };

  // Get company initials for fallback
  const getCompanyInitials = (name?: string): string => {
    if (!name) return '?';

    // Split by spaces and take first letter of each word, max 2 letters
    const words = name.trim().split(/\s+/);
    if (words.length === 1) {
      // Single word - take first letter
      return words[0].charAt(0).toUpperCase();
    } else {
      // Multiple words - take first letter of first two words
      return words
        .slice(0, 2)
        .map(word => word.charAt(0).toUpperCase())
        .join('');
    }
  };

  useEffect(() => {
    if (!domain || faviconError) return;

    const cleanDomain = getDomainFromUrl(domain);

    // Prioritize Google's favicon service for better reliability
    const faviconSources = [
      `https://www.google.com/s2/favicons?domain=${cleanDomain}&sz=64`,
      `https://www.google.com/s2/favicons?domain=${cleanDomain}&sz=32`,
      `https://${cleanDomain}/favicon.ico`,
      `https://icons.duckduckgo.com/ip3/${cleanDomain}.ico`,
      `https://favicon.im/${cleanDomain}?larger=true`,
    ];

    console.log('Trying favicon sources for', cleanDomain, ':', faviconSources);

    let currentIndex = 0;

    const tryNextFavicon = () => {
      if (currentIndex >= faviconSources.length) {
        console.log('All favicon sources failed for', cleanDomain);
        setFaviconError(true);
        return;
      }

      const img = new Image();
      // Remove crossOrigin to avoid CORS issues
      // img.crossOrigin = 'anonymous';

      // Set a shorter timeout to avoid hanging
      const timeoutId = setTimeout(() => {
        if (!img.complete) {
          console.log('Favicon timeout:', faviconSources[currentIndex]);
          img.src = ''; // Cancel the request
          currentIndex++;
          tryNextFavicon();
        }
      }, 2000);

      img.onload = () => {
        clearTimeout(timeoutId);
        console.log('Favicon loaded successfully:', faviconSources[currentIndex]);
        // Check if the image has actual content (not a placeholder)
        if (img.width > 1 && img.height > 1) {
          setFaviconUrl(faviconSources[currentIndex]);
        } else {
          console.log('Favicon too small, trying next:', faviconSources[currentIndex]);
          currentIndex++;
          tryNextFavicon();
        }
      };

      img.onerror = () => {
        clearTimeout(timeoutId);
        console.log('Favicon failed to load:', faviconSources[currentIndex]);
        currentIndex++;
        tryNextFavicon();
      };

      img.src = faviconSources[currentIndex];
    };

    tryNextFavicon();
  }, [domain, faviconError]);

  const defaultSx: SxProps<Theme> = {
    width: size,
    height: size,
    bgcolor: '#FF9800', // Orange color
    color: '#FFFFFF', // CI: $color-white
    fontSize: size > 48 ? '1.5rem' : '1rem',
    fontWeight: 'bold',
  };

  const combinedSx = { ...defaultSx, ...sx };

  // If we have a favicon and no error, show favicon
  if (faviconUrl && !faviconError) {
    return (
      <Avatar src={faviconUrl} sx={combinedSx} onError={() => setFaviconError(true)}>
        {getCompanyInitials(companyName)}
      </Avatar>
    );
  }

  // Fallback to initials
  return <Avatar sx={combinedSx}>{getCompanyInitials(companyName)}</Avatar>;
};

export default CompanyAvatar;
